// Popular program templates that can be imported and auto-built into the
// app's per-lift program format.
//
// The app stores one program per lift (bench / squat / deadlift). Well-known
// novice and intermediate programs are whole-week, all-lifts templates, so a
// template generator returns one program per lift and the importer activates
// all of them together. Generated programs are tagged with templateId and
// importGroup so they can be recognised as a set.
//
// Generated exercises carry an absolute numeric `weight` (kg) rather than a
// percentage, because these programs prescribe their own progression
// ("add 2.5kg to last session"), not a percentage of a training max.
// Overhead press has no slot of its own, so it lives inside the bench
// program — the convention throughout this file.
//
// Template contract:
//   meta: id, name, author, level, frequency, summary, structure[], caveats[]
//   options: startLabels (which starting-weight fields to show),
//            incrementKeys (per-session/week increment pickers, or null),
//            toggles[] ({key, label, help, choices:[{value,label}]}),
//            weeksRange ([min, max], or null when length is toggle-driven)
//   getDefaults(settings) -> config     (includes rounding)
//   generate(config) -> { bench, squat, deadlift }  (program objects)

import { roundWeight } from '../utils/calculations';

// Derived press strength: a trained overhead press is roughly two-thirds
// of a bench press. Used wherever a template needs a press number.
const PRESS_FROM_BENCH = 0.65;

// ~5RM as a fraction of 1RM (Epley)
const FIVE_RM = 0.85;

function ex(name, weight, sets, reps, extra) {
  return { name, weight, sets, reps, ...(extra || {}) };
}

function programShell(group, templateId, lift, displayName, level, daysPerWeek, weeks) {
  return {
    id: `${group}_${lift}`,
    lift,
    frequency: `${daysPerWeek}x`,
    level,
    displayName,
    isCustom: true,
    templateId,
    importGroup: group,
    weeks,
  };
}

// =====================================================
// STARTING STRENGTH  (novice)
// =====================================================
// A: Squat 3x5, Press 3x5, Deadlift 1x5
// B: Squat 3x5, Bench 3x5, Deadlift 1x5
// Alternate A/B across 3 sessions/week; every lift adds weight every
// time it is performed.

const startingStrength = {
  id: 'starting_strength',
  name: 'Starting Strength',
  author: 'Mark Rippetoe',
  level: 'Novice',
  frequency: '3x/week · full body',
  summary: 'Three full-body sessions a week alternating Workout A and Workout B. ' +
    'Add weight to every lift every session, for as long as that keeps working.',
  structure: [
    'Workout A — Squat 3×5, Overhead Press 3×5, Deadlift 1×5',
    'Workout B — Squat 3×5, Bench Press 3×5, Deadlift 1×5',
    'Alternate A/B across three sessions (A-B-A, then B-A-B)',
  ],
  caveats: [
    'The generated weeks are a fixed ramp. Starting Strength normally deloads 10% ' +
      'after three failed attempts at a weight — if you stall, re-import with new starting weights.',
    'Overhead Press shares the bench program and appears on Workout A days.',
  ],
  options: {
    startLabels: ['squat', 'bench', 'press', 'deadlift'],
    incrementKeys: ['squat', 'bench', 'press', 'deadlift'],
    toggles: [{
      key: 'deadliftFrequency',
      label: 'Deadlift frequency',
      help: 'The book deadlifts every session, but at three sessions a week the weight ' +
        'outruns most novices within a month. Once a week is how it is usually run.',
      choices: [
        { value: 'weekly', label: 'Once a week' },
        { value: 'every', label: 'Every session' },
      ],
    }],
    weeksRange: [4, 24],
  },
  getDefaults(settings) {
    const rounding = settings.rounding || 2.5;
    const benchStart = roundWeight((settings.bench1RM || 0) * 0.50, rounding);
    return {
      rounding,
      starts: {
        squat: roundWeight((settings.squat1RM || 0) * 0.50, rounding),
        bench: benchStart,
        press: roundWeight(benchStart * PRESS_FROM_BENCH, rounding),
        deadlift: roundWeight((settings.deadlift1RM || 0) * 0.55, rounding),
      },
      increments: { squat: 2.5, bench: 2.5, press: 2.5, deadlift: 5 },
      weeks: 12,
      deadliftFrequency: 'weekly',
    };
  },
  generate(config) {
    const { starts, increments, weeks, deadliftFrequency } = config;
    const group = `ss_${Date.now()}`;
    const squatWeeks = [], benchWeeks = [], deadliftWeeks = [];
    let squatN = 0, benchN = 0, pressN = 0, deadliftN = 0;

    for (let w = 0; w < weeks; w++) {
      const squatDays = [], benchDays = [], deadliftDays = [];
      for (let d = 0; d < 3; d++) {
        const isA = (w * 3 + d) % 2 === 0;
        squatDays.push({ dayNum: d + 1, exercises: [
          ex('Squat', starts.squat + increments.squat * squatN++, 3, '5'),
        ]});
        if (isA) {
          benchDays.push({ dayNum: d + 1, exercises: [
            ex('Overhead Press', starts.press + increments.press * pressN++, 3, '5', { notes: 'Workout A' }),
          ]});
        } else {
          benchDays.push({ dayNum: d + 1, exercises: [
            ex('Bench Press', starts.bench + increments.bench * benchN++, 3, '5', { notes: 'Workout B' }),
          ]});
        }
        if (deadliftFrequency === 'every' || d === 2) {
          deadliftDays.push({ dayNum: deadliftDays.length + 1, exercises: [
            ex('Deadlift', starts.deadlift + increments.deadlift * deadliftN++, 1, '5'),
          ]});
        }
      }
      squatWeeks.push({ weekNum: w + 1, days: squatDays });
      benchWeeks.push({ weekNum: w + 1, days: benchDays });
      deadliftWeeks.push({ weekNum: w + 1, days: deadliftDays });
    }

    const dlDays = deadliftFrequency === 'every' ? 3 : 1;
    return {
      squat: programShell(group, this.id, 'squat', 'Starting Strength — Squat', 'Starting Strength', 3, squatWeeks),
      bench: programShell(group, this.id, 'bench', 'Starting Strength — Bench / Press', 'Starting Strength', 3, benchWeeks),
      deadlift: programShell(group, this.id, 'deadlift', 'Starting Strength — Deadlift', 'Starting Strength', dlDays, deadliftWeeks),
    };
  },
};

// =====================================================
// STRONGLIFTS 5x5  (novice)
// =====================================================
// A: Squat 5x5, Bench 5x5, Barbell Row 5x5
// B: Squat 5x5, Overhead Press 5x5, Deadlift 1x5
// Alternate A/B, 3 sessions/week, add weight every time a lift is performed.

const strongLifts = {
  id: 'stronglifts_5x5',
  name: 'StrongLifts 5×5',
  author: 'Mehdi Hadim',
  level: 'Novice',
  frequency: '3x/week · full body',
  summary: 'The classic 5×5 linear progression. Two alternating full-body workouts, ' +
    'three sessions a week, +2.5kg every time a lift is performed.',
  structure: [
    'Workout A — Squat 5×5, Bench Press 5×5, Barbell Row 5×5',
    'Workout B — Squat 5×5, Overhead Press 5×5, Deadlift 1×5',
    'Alternate A/B across three sessions (A-B-A, then B-A-B)',
  ],
  caveats: [
    'The generated weeks are a fixed ramp with no stall handling. The official rule is ' +
      'deload 10% on a lift after three failed sessions — if you stall, re-import from lighter starts.',
    'Overhead Press shares the bench program (Workout B days); Barbell Rows ride along ' +
      'on Workout A days.',
    'Deadlift lands on Workout B days, so it alternates between once and twice a week.',
  ],
  options: {
    startLabels: ['squat', 'bench', 'press', 'deadlift', 'row'],
    incrementKeys: ['squat', 'bench', 'press', 'deadlift', 'row'],
    toggles: [],
    weeksRange: [4, 24],
  },
  getDefaults(settings) {
    const rounding = settings.rounding || 2.5;
    const benchStart = roundWeight((settings.bench1RM || 0) * 0.45, rounding);
    return {
      rounding,
      starts: {
        squat: roundWeight((settings.squat1RM || 0) * 0.45, rounding),
        bench: benchStart,
        press: roundWeight(benchStart * PRESS_FROM_BENCH, rounding),
        deadlift: roundWeight((settings.deadlift1RM || 0) * 0.50, rounding),
        row: roundWeight(benchStart * PRESS_FROM_BENCH, rounding),
      },
      increments: { squat: 2.5, bench: 2.5, press: 2.5, deadlift: 5, row: 2.5 },
      weeks: 12,
    };
  },
  generate(config) {
    const { starts, increments, weeks } = config;
    const group = `sl_${Date.now()}`;
    const squatWeeks = [], benchWeeks = [], deadliftWeeks = [];
    let squatN = 0, benchN = 0, pressN = 0, deadliftN = 0, rowN = 0;

    for (let w = 0; w < weeks; w++) {
      const squatDays = [], benchDays = [], deadliftDays = [];
      for (let d = 0; d < 3; d++) {
        const isA = (w * 3 + d) % 2 === 0;
        squatDays.push({ dayNum: d + 1, exercises: [
          ex('Squat', starts.squat + increments.squat * squatN++, 5, '5'),
        ]});
        if (isA) {
          benchDays.push({ dayNum: d + 1, exercises: [
            ex('Bench Press', starts.bench + increments.bench * benchN++, 5, '5', { notes: 'Workout A' }),
            ex('Barbell Rows', starts.row + increments.row * rowN++, 5, '5', { isAccessory: true }),
          ]});
        } else {
          benchDays.push({ dayNum: d + 1, exercises: [
            ex('Overhead Press', starts.press + increments.press * pressN++, 5, '5', { notes: 'Workout B' }),
          ]});
          deadliftDays.push({ dayNum: deadliftDays.length + 1, exercises: [
            ex('Deadlift', starts.deadlift + increments.deadlift * deadliftN++, 1, '5'),
          ]});
        }
      }
      squatWeeks.push({ weekNum: w + 1, days: squatDays });
      benchWeeks.push({ weekNum: w + 1, days: benchDays });
      deadliftWeeks.push({ weekNum: w + 1, days: deadliftDays });
    }

    return {
      squat: programShell(group, this.id, 'squat', 'StrongLifts 5×5 — Squat', 'StrongLifts', 3, squatWeeks),
      bench: programShell(group, this.id, 'bench', 'StrongLifts 5×5 — Bench / Press', 'StrongLifts', 3, benchWeeks),
      deadlift: programShell(group, this.id, 'deadlift', 'StrongLifts 5×5 — Deadlift', 'StrongLifts', 2, deadliftWeeks),
    };
  },
};

// =====================================================
// TEXAS METHOD  (intermediate)
// =====================================================
// Weekly cycle per lift: Monday volume 5x5 @ 90% of Friday's target,
// Wednesday light, Friday 1x5 PR (+2.5kg/week).
// Bench and press alternate weeks in the book version.

const texasMethod = {
  id: 'texas_method',
  name: 'Texas Method',
  author: 'Mark Rippetoe / Glenn Pendlay',
  level: 'Intermediate',
  frequency: '3x/week · volume / light / intensity',
  summary: 'The classic first intermediate program: high-volume Monday, light recovery ' +
    'Wednesday, and a single 5-rep PR attempt every Friday. Progress moves weekly ' +
    'instead of every session.',
  structure: [
    'Monday (volume) — Squat 5×5 and Bench/Press 5×5 at 90% of Friday\'s weight',
    'Wednesday (light) — Squat 2×5 light, the other press light, chins & back work',
    'Friday (intensity) — Squat 1×5 PR, Bench/Press 1×5 PR, Deadlift 1×5 PR',
    'Bench and Overhead Press alternate weeks (book version)',
  ],
  caveats: [
    'Your first Friday should land around your current 5RM — the seeded weights assume that.',
    'The book alternates deadlifts with power cleans on Friday; this build deadlifts every week.',
    'No stall handling: when Friday stops moving, the usual fixes are more Monday volume ' +
      'or switching Friday to triples — re-import or edit when you get there.',
  ],
  options: {
    startLabels: ['squat', 'bench', 'press', 'deadlift'],
    incrementKeys: ['squat', 'bench', 'press', 'deadlift'],
    toggles: [{
      key: 'pressMode',
      label: 'Overhead press',
      help: 'The book alternates bench and press weekly. Bench-only runs bench all three ' +
        'days every week — common for powerlifting meets.',
      choices: [
        { value: 'alternate', label: 'Alternate weekly (book)' },
        { value: 'none', label: 'Bench only' },
      ],
    }],
    weeksRange: [4, 16],
  },
  getDefaults(settings) {
    const rounding = settings.rounding || 2.5;
    return {
      rounding,
      starts: {
        squat: roundWeight((settings.squat1RM || 0) * FIVE_RM, rounding),
        bench: roundWeight((settings.bench1RM || 0) * FIVE_RM, rounding),
        press: roundWeight((settings.bench1RM || 0) * PRESS_FROM_BENCH * FIVE_RM, rounding),
        deadlift: roundWeight((settings.deadlift1RM || 0) * FIVE_RM, rounding),
      },
      increments: { squat: 2.5, bench: 2.5, press: 2.5, deadlift: 2.5 },
      weeks: 10,
      pressMode: 'alternate',
    };
  },
  generate(config) {
    const { starts, increments, weeks, rounding, pressMode } = config;
    const group = `tm_${Date.now()}`;
    const r = (v) => roundWeight(v, rounding);
    const squatWeeks = [], benchWeeks = [], deadliftWeeks = [];

    for (let w = 0; w < weeks; w++) {
      // Squat: PRs every week
      const fSq = starts.squat + increments.squat * w;
      squatWeeks.push({ weekNum: w + 1, days: [
        { dayNum: 1, exercises: [
          ex('Squat', r(fSq * 0.90), 5, '5', { notes: 'Volume day — 90% of Friday' }),
        ]},
        { dayNum: 2, exercises: [
          ex('Squat', r(fSq * 0.72), 2, '5', { notes: 'Light day — recovery, keep it crisp' }),
          ex('Back Extensions', undefined, 3, '10', { isAccessory: true }),
        ]},
        { dayNum: 3, exercises: [
          ex('Squat', r(fSq), 1, '5', { notes: '5RM PR attempt' }),
        ]},
      ]});

      // Bench / press
      let mon, wed, fri;
      if (pressMode === 'none') {
        const fB = starts.bench + increments.bench * w;
        mon = ex('Bench Press', r(fB * 0.90), 5, '5', { notes: 'Volume day — 90% of Friday' });
        wed = ex('Bench Press', r(fB * 0.72), 3, '5', { notes: 'Light day' });
        fri = ex('Bench Press', r(fB), 1, '5', { notes: '5RM PR attempt' });
      } else {
        const isBenchWeek = w % 2 === 0;
        const kBench = Math.floor(w / 2);
        const kPress = Math.floor(w / 2);
        const fB = starts.bench + increments.bench * kBench;
        const fP = starts.press + increments.press * kPress;
        if (isBenchWeek) {
          mon = ex('Bench Press', r(fB * 0.90), 5, '5', { notes: 'Bench week — volume, 90% of Friday' });
          wed = ex('Overhead Press', r(fP * 0.72), 3, '5', { notes: 'Light day' });
          fri = ex('Bench Press', r(fB), 1, '5', { notes: '5RM PR attempt' });
        } else {
          mon = ex('Overhead Press', r(fP * 0.90), 5, '5', { notes: 'Press week — volume, 90% of Friday' });
          wed = ex('Bench Press', r(fB * 0.72), 3, '5', { notes: 'Light day' });
          fri = ex('Overhead Press', r(fP), 1, '5', { notes: '5RM PR attempt' });
        }
      }
      benchWeeks.push({ weekNum: w + 1, days: [
        { dayNum: 1, exercises: [mon] },
        { dayNum: 2, exercises: [wed, ex('Chin-ups', undefined, 3, 'AMAP', { isAccessory: true })] },
        { dayNum: 3, exercises: [fri] },
      ]});

      // Deadlift: Friday PR only
      const fDl = starts.deadlift + increments.deadlift * w;
      deadliftWeeks.push({ weekNum: w + 1, days: [
        { dayNum: 1, exercises: [
          ex('Deadlift', r(fDl), 1, '5', { notes: 'Friday intensity — 5RM PR attempt' }),
        ]},
      ]});
    }

    return {
      squat: programShell(group, this.id, 'squat', 'Texas Method — Squat', 'Texas Method', 3, squatWeeks),
      bench: programShell(group, this.id, 'bench', 'Texas Method — Bench / Press', 'Texas Method', 3, benchWeeks),
      deadlift: programShell(group, this.id, 'deadlift', 'Texas Method — Deadlift', 'Texas Method', 1, deadliftWeeks),
    };
  },
};

// =====================================================
// MADCOW 5x5  (intermediate)
// =====================================================
// Ramping 5x5 with weekly top-set progression (+2.5%/week; your old 5RM
// falls on week 4). Friday adds a PR triple at next Monday's weight.

const MADCOW_RAMP = [0.50, 0.625, 0.75, 0.875, 1.0];

const madcow = {
  id: 'madcow_5x5',
  name: 'Madcow 5×5',
  author: 'Bill Starr / "Madcow"',
  level: 'Intermediate',
  frequency: '3x/week · ramping sets',
  summary: 'The classic bridge out of novice linear progression. Every set ramps up to ' +
    'one heavy top set; top sets climb 2.5% a week, with a PR triple every Friday.',
  structure: [
    'Monday — Squat, Bench, Rows: 5×5 ramping to the week\'s top set',
    'Wednesday — light Squat 4×5, Overhead Press, Deadlift ramping to a top set',
    'Friday — ramp again, then 1×3 PR (next Monday\'s weight) and a 1×8 back-off',
    'Top sets +2.5% weekly — week 4 matches your current 5RM, PRs from week 5',
  ],
  caveats: [
    'Weights are seeded so week 4 hits your current 5RM; weeks 1-3 should feel like ' +
      'a run-up, not a grind.',
    'Overhead Press shares the bench program (Wednesdays); Rows ride along Mon/Fri.',
    'If a top set stalls, the usual fix is to reset that lift 2-3 weeks back — re-import ' +
      'with adjusted starts when you get there.',
  ],
  options: {
    startLabels: ['squat', 'bench', 'press', 'deadlift', 'row'],
    incrementKeys: null, // progression is the fixed +2.5%/week ramp
    toggles: [],
    weeksRange: [4, 16],
  },
  getDefaults(settings) {
    const rounding = settings.rounding || 2.5;
    // starts = week-1 top set (92.5% of current 5RM; 5RM lands week 4)
    const base = (oneRM) => roundWeight(oneRM * FIVE_RM * 0.925, rounding);
    return {
      rounding,
      starts: {
        squat: base(settings.squat1RM || 0),
        bench: base(settings.bench1RM || 0),
        press: base((settings.bench1RM || 0) * PRESS_FROM_BENCH),
        deadlift: base(settings.deadlift1RM || 0),
        row: base((settings.bench1RM || 0) * 0.55),
      },
      weeks: 10,
    };
  },
  generate(config) {
    const { starts, weeks, rounding } = config;
    const group = `mc_${Date.now()}`;
    const r = (v) => roundWeight(v, rounding);
    // weekly top set: start grows by 2.5% of start per week (linear)
    const top = (startVal, w) => startVal * (1 + 0.025 * w);
    const squatWeeks = [], benchWeeks = [], deadliftWeeks = [];

    for (let w = 0; w < weeks; w++) {
      const tSq = top(starts.squat, w);
      const tB = top(starts.bench, w);
      const tP = top(starts.press, w);
      const tDl = top(starts.deadlift, w);
      const tRow = top(starts.row, w);

      const ramp5 = (name, t) => MADCOW_RAMP.map((p, i) =>
        ex(name, r(t * p), 1, '5', i === MADCOW_RAMP.length - 1 ? { notes: 'Top set' } : undefined));
      const friday = (name, t) => [
        ...MADCOW_RAMP.slice(0, 4).map(p => ex(name, r(t * p), 1, '5')),
        ex(name, r(t * 1.025), 1, '3', { notes: 'PR triple — next Monday\'s top weight' }),
        ex(name, r(t * 0.75), 1, '8', { notes: 'Back-off set' }),
      ];

      squatWeeks.push({ weekNum: w + 1, days: [
        { dayNum: 1, exercises: ramp5('Squat', tSq) },
        { dayNum: 2, exercises: [0.50, 0.625, 0.75, 0.75].map((p, i) =>
          ex('Squat', r(tSq * p), 1, '5', i === 3 ? { notes: 'Light day — stop here' } : undefined)) },
        { dayNum: 3, exercises: friday('Squat', tSq) },
      ]});

      benchWeeks.push({ weekNum: w + 1, days: [
        { dayNum: 1, exercises: [
          ...ramp5('Bench Press', tB),
          ex('Barbell Rows', r(tRow), 5, '5', { isAccessory: true, notes: 'Ramp 50/62.5/75/87.5/100%' }),
        ]},
        { dayNum: 2, exercises: [0.50, 0.65, 0.80, 1.0].map((p, i) =>
          ex('Overhead Press', r(tP * p), 1, '5', i === 3 ? { notes: 'Top set' } : undefined)) },
        { dayNum: 3, exercises: [
          ...friday('Bench Press', tB),
          ex('Barbell Rows', r(tRow * 1.025), 4, '5', { isAccessory: true, notes: 'Ramp, then 1×3 PR' }),
        ]},
      ]});

      deadliftWeeks.push({ weekNum: w + 1, days: [
        { dayNum: 1, exercises: [0.50, 0.625, 0.75, 1.0].map((p, i) =>
          ex('Deadlift', r(tDl * p), 1, '5', i === 3 ? { notes: 'Top set' } : undefined)) },
      ]});
    }

    return {
      squat: programShell(group, this.id, 'squat', 'Madcow 5×5 — Squat', 'Madcow', 3, squatWeeks),
      bench: programShell(group, this.id, 'bench', 'Madcow 5×5 — Bench / Press', 'Madcow', 3, benchWeeks),
      deadlift: programShell(group, this.id, 'deadlift', 'Madcow 5×5 — Deadlift', 'Madcow', 1, deadliftWeeks),
    };
  },
};

// =====================================================
// WENDLER 5/3/1 (Boring But Big)  (intermediate)
// =====================================================
// 4-week waves off a training max (90% of 1RM): 5s week, 3s week, 5/3/1
// week, deload. Last main set each day is AMRAP. BBB adds 5x10 volume.

const W531_WEEKS = [
  { label: '5s week', sets: [[0.65, '5'], [0.75, '5'], [0.85, '5']], amrapTarget: 5 },
  { label: '3s week', sets: [[0.70, '3'], [0.80, '3'], [0.90, '3']], amrapTarget: 3 },
  { label: '5/3/1 week', sets: [[0.75, '5'], [0.85, '3'], [0.95, '1']], amrapTarget: 1 },
];
const W531_DELOAD = [[0.40, '5'], [0.50, '5'], [0.60, '5']];

const wendler531 = {
  id: 'wendler_531',
  name: '5/3/1 — Boring But Big',
  author: 'Jim Wendler',
  level: 'Intermediate',
  frequency: '4 lifts/week · 4-week waves',
  summary: 'Slow, sustainable strength. Each lift gets one day a week: three work sets ' +
    'ending in an all-out AMRAP, then 5×10 volume work. Training max rises every ' +
    '4-week cycle.',
  structure: [
    'Week 1 — 65/75/85% ×5, last set AMRAP',
    'Week 2 — 70/80/90% ×3, last set AMRAP',
    'Week 3 — 75/85/95% ×5/3/1, last set AMRAP',
    'Week 4 — deload 40/50/60% ×5',
    'All % of a training max = 90% of your 1RM; TM +2.5kg upper / +5kg lower per cycle',
    'BBB: 5×10 at 50% TM of the same lift after the main work',
  ],
  caveats: [
    'Percentages run off the training max (90% of 1RM), not your true max — the top ' +
      'sets are meant to leave reps in the tank. Trust it; the AMRAP sets are where you push.',
    'Overhead Press gets its own day inside the bench program (day 2).',
    'Beat the rep target on AMRAP sets. If you can\'t even match it, the TM is too high — ' +
      're-import with a lower 1RM.',
  ],
  options: {
    startLabels: null, // computed from 1RM; no manual starting weights
    incrementKeys: null,
    toggles: [
      {
        key: 'cycles',
        label: 'Cycles',
        help: 'Each cycle is 4 weeks (3 with deloads off). The training max auto-increases each cycle.',
        choices: [
          { value: 2, label: '2 cycles' },
          { value: 3, label: '3 cycles' },
          { value: 4, label: '4 cycles' },
        ],
      },
      {
        key: 'bbb',
        label: 'Boring But Big 5×10',
        help: 'The signature volume work: 5×10 of the same lift at 50% TM after the main sets.',
        choices: [
          { value: true, label: 'Include' },
          { value: false, label: 'Main work only' },
        ],
      },
      {
        key: 'deload',
        label: 'Deload weeks',
        help: 'Week 4 of each cycle at 40-60%. Wendler considers them part of the program.',
        choices: [
          { value: true, label: 'Include' },
          { value: false, label: 'Skip' },
        ],
      },
    ],
    weeksRange: null, // length = cycles × (3 or 4)
  },
  getDefaults(settings) {
    const rounding = settings.rounding || 2.5;
    return {
      rounding,
      oneRMs: {
        squat: settings.squat1RM || 0,
        bench: settings.bench1RM || 0,
        deadlift: settings.deadlift1RM || 0,
        press: (settings.bench1RM || 0) * PRESS_FROM_BENCH,
      },
      cycles: 3,
      bbb: true,
      deload: true,
    };
  },
  generate(config) {
    const { oneRMs, cycles, bbb, deload, rounding } = config;
    const group = `w531_${Date.now()}`;
    const r = (v) => roundWeight(v, rounding);
    const tmInc = { squat: 5, deadlift: 5, bench: 2.5, press: 2.5 };
    const tm = (lift, c) => (oneRMs[lift] * 0.90) + tmInc[lift] * c;

    const mainSets = (name, t, weekDef) => {
      const sets = weekDef.sets.map(([p, reps], i) => {
        if (i < weekDef.sets.length - 1) return ex(name, r(t * p), 1, reps);
        return ex(name, r(t * p), 1, 'AMAP', {
          isAMAP: true,
          notes: `AMRAP — beat ${weekDef.amrapTarget} rep${weekDef.amrapTarget > 1 ? 's' : ''}`,
        });
      });
      if (bbb) sets.push(ex(name, r(t * 0.50), 5, '10', { notes: 'Boring But Big' }));
      return sets;
    };

    const squatWeeks = [], benchWeeks = [], deadliftWeeks = [];
    let weekNum = 0;

    for (let c = 0; c < cycles; c++) {
      const defs = deload ? [...W531_WEEKS, null] : W531_WEEKS;
      for (const weekDef of defs) {
        weekNum++;
        if (weekDef === null) {
          const deloadSets = (name, t) => W531_DELOAD.map(([p, reps]) => ex(name, r(t * p), 1, reps));
          squatWeeks.push({ weekNum, days: [
            { dayNum: 1, exercises: deloadSets('Squat', tm('squat', c)) },
          ]});
          benchWeeks.push({ weekNum, days: [
            { dayNum: 1, exercises: deloadSets('Bench Press', tm('bench', c)) },
            { dayNum: 2, exercises: deloadSets('Overhead Press', tm('press', c)) },
          ]});
          deadliftWeeks.push({ weekNum, days: [
            { dayNum: 1, exercises: deloadSets('Deadlift', tm('deadlift', c)) },
          ]});
          continue;
        }
        squatWeeks.push({ weekNum, days: [
          { dayNum: 1, exercises: [
            ...mainSets('Squat', tm('squat', c), weekDef),
            ex('Abs/Core work', undefined, 5, '10-15', { isAccessory: true }),
          ]},
        ]});
        benchWeeks.push({ weekNum, days: [
          { dayNum: 1, exercises: [
            ...mainSets('Bench Press', tm('bench', c), weekDef),
            ex('Chins/Rows', undefined, 5, '10', { isAccessory: true }),
          ]},
          { dayNum: 2, exercises: [
            ...mainSets('Overhead Press', tm('press', c), weekDef),
            ex('Dips/Push-ups', undefined, 5, '10', { isAccessory: true }),
          ]},
        ]});
        deadliftWeeks.push({ weekNum, days: [
          { dayNum: 1, exercises: [
            ...mainSets('Deadlift', tm('deadlift', c), weekDef),
            ex('Hanging Leg Raises', undefined, 5, '10', { isAccessory: true }),
          ]},
        ]});
      }
    }

    return {
      squat: programShell(group, this.id, 'squat', '5/3/1 BBB — Squat', '5/3/1', 1, squatWeeks),
      bench: programShell(group, this.id, 'bench', '5/3/1 BBB — Bench / Press', '5/3/1', 2, benchWeeks),
      deadlift: programShell(group, this.id, 'deadlift', '5/3/1 BBB — Deadlift', '5/3/1', 1, deadliftWeeks),
    };
  },
};

// =====================================================
// CATALOGUE
// =====================================================

const templates = [
  startingStrength,
  strongLifts,
  texasMethod,
  madcow,
  wendler531,
];

export function getTemplates() {
  return templates;
}

export function getTemplateById(id) {
  return templates.find(t => t.id === id) || null;
}

export default templates;
